class CardMenu {
    constructor(templateSelector = "[data-card-menu-template]", config = {}) {
        /** @type {HTMLElement} */
        this.template = document.querySelector(templateSelector);
        if (!this.template) return;

        this.options = {
            closer: '[data-card-menu-close]',
            trigger: '[data-card-menu-trigger]',
            domain: '[data-card-menu-domain]',
            siteSearch: '[data-card-menu-site-search]',
            report: '[data-card-menu-report]',
            promote: '[data-card-menu-promote]',
            openedClass: '_opened',
            debug: config.debug ?? false,
            apiEndpoint: config.apiEndpoint,
            apiPromoteInfo: config.apiPromoteInfo,
            apiPromote: config.apiPromote,
            promoteAgreementUrl: config.promoteAgreementUrl || '/docs/promote-agreement.pdf',
            messages: {
                reportSuccess: 'Спасибо за сообщение о проблеме с сайтом {domain}',
                reportError: 'Не удалось отправить сообщение. Попробуйте позже.',
                ...config.messages
            },
            ...config
        };

        this.currentCardId = null;
        this.currentCardElement = null;
        this.triggerElement = null;
        this.promoteModal = null;

        // ARIA: изначально меню скрыто
        this.template.setAttribute('aria-hidden', 'true');
        this.template.setAttribute('role', 'dialog');
        this.template.setAttribute('aria-modal', 'true');

        this.init();
    }

    init() {
        // Close on background click
        this.template.addEventListener('click', (event) => {
            if (event.target === this.template) {
                this.close();
            }
        });

        // Close button
        const closer = this.template.querySelector(this.options.closer);
        if (closer) {
            closer.addEventListener('click', (e) => {
                e.stopPropagation();
                this.close();
            });
        }

        // Site search button
        const siteSearch = this.template.querySelector(this.options.siteSearch);
        if (siteSearch) {
            siteSearch.addEventListener('click', (e) => {
                e.stopPropagation();
                this.onSiteSearch();
            });
        }

        // Report button
        const report = this.template.querySelector(this.options.report);
        if (report) {
            report.addEventListener('click', (e) => {
                e.stopPropagation();
                this.onReport();
            });
        }

        // Promote button
        const promote = this.template.querySelector(this.options.promote);
        if (promote) {
            promote.addEventListener('click', (e) => {
                e.stopPropagation();
                this.onPromote();
            });
        }

        // Setup triggers (delegated event for dynamic cards)
        document.addEventListener('click', (e) => {
            const trigger = e.target.closest(this.options.trigger);
            if (trigger) {
                e.preventDefault();
                e.stopPropagation();
                const cardId = trigger.getAttribute('data-card-menu-trigger');
                const card = trigger.closest('.results__card');
                this.open(cardId, card, trigger);
            }
        });

        // Закрытие по Escape и фокус-трап для Tab
        document.addEventListener('keydown', (e) => {
            if (!this.template.classList.contains(this.options.openedClass)) return;

            if (e.key === 'Escape') {
                this.close();
                return;
            }

            // Фокус-трап: Tab не выходит за пределы меню
            if (e.key === 'Tab') {
                const content = this.template.querySelector('.card-menu__content');
                if (!content) return;

                const focusableElements = content.querySelectorAll(
                    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"]), li[data-card-menu-site-search], li[data-card-menu-report], [data-card-menu-close]'
                );
                const firstElement = focusableElements[0];
                const lastElement = focusableElements[focusableElements.length - 1];

                if (e.shiftKey) {
                    // Shift+Tab: если на первом элементе — перейти на последний
                    if (document.activeElement === firstElement) {
                        e.preventDefault();
                        lastElement?.focus();
                    }
                } else {
                    // Tab: если на последнем элементе — перейти на первый
                    if (document.activeElement === lastElement) {
                        e.preventDefault();
                        firstElement?.focus();
                    }
                }
            }
        });

        // Swipe down to close on mobile
        this.initSwipeToClose();
    }

    initSwipeToClose() {
        // Only init on mobile
        if (window.innerWidth > 768) return;

        const content = this.template.querySelector('.card-menu__content');
        if (!content) return;

        let startY = 0;
        let currentY = 0;
        let isDragging = false;

        const onTouchStart = (e) => {
            if (!this.template.classList.contains(this.options.openedClass)) return;

            startY = e.touches[0].clientY;
            currentY = startY;
            isDragging = true;
            content.style.transition = 'none';
        };

        const onTouchMove = (e) => {
            if (!isDragging) return;

            currentY = e.touches[0].clientY;
            const deltaY = currentY - startY;

            // Only allow dragging down
            if (deltaY > 0) {
                content.style.transform = `translateY(${deltaY}px)`;

                // Fade background based on drag distance
                const opacity = Math.max(0, 0.4 - (deltaY / 500));
                this.template.style.backgroundColor = `rgba(0, 0, 0, ${opacity})`;
            }
        };

        const onTouchEnd = () => {
            if (!isDragging) return;
            isDragging = false;

            const deltaY = currentY - startY;
            content.style.transition = '';
            this.template.style.transition = '';

            // If dragged more than 80px down, close the menu
            if (deltaY > 80) {
                this.close();
            } else {
                // Snap back
                content.style.transform = '';
                this.template.style.backgroundColor = '';
            }
        };

        content.addEventListener('touchstart', onTouchStart, { passive: true });
        content.addEventListener('touchmove', onTouchMove, { passive: true });
        content.addEventListener('touchend', onTouchEnd);
        content.addEventListener('touchcancel', onTouchEnd);
    }

    open(cardId, cardElement, triggerElement) {
        this.currentCardId = cardId;
        this.currentCardElement = cardElement;
        this.triggerElement = triggerElement; // Сохраняем для возврата фокуса

        // Получаем домен из карточки
        const domainElement = cardElement?.querySelector('.results__card-domain');
        const domain = domainElement?.textContent?.trim() || '';

        // Обновляем заголовок меню
        const heading = this.template.querySelector(this.options.domain);
        if (heading) {
            heading.textContent = domain;
        }

        // Позиционируем меню
        this.positionMenu(triggerElement);

        // Открываем меню
        this.template.classList.add(this.options.openedClass);

        // Блокируем скролл body (особенно важно на мобильных)
        document.body.style.overflow = 'hidden';
        document.body.style.touchAction = 'none';

        // ARIA: помечаем меню как открытое
        this.template.setAttribute('aria-hidden', 'false');

        // Устанавливаем фокус на первый интерактивный элемент
        const content = this.template.querySelector('.card-menu__content');
        if (content) {
            const firstFocusable = content.querySelector('button, [data-card-menu-close], li');
            if (firstFocusable) {
                setTimeout(() => firstFocusable.focus(), 100);
            }
        }
    }

    close() {
        this.template.classList.remove(this.options.openedClass);

        // Разблокируем скролл body
        document.body.style.overflow = '';
        document.body.style.touchAction = '';

        // ARIA: помечаем меню как скрытое
        this.template.setAttribute('aria-hidden', 'true');

        // Возвращаем фокус на элемент, открывший меню
        if (this.triggerElement) {
            this.triggerElement.focus();
        }

        this.currentCardId = null;
        this.currentCardElement = null;
        this.triggerElement = null;

        const content = this.template.querySelector('.card-menu__content');

        // Сбрасываем стили свайпа
        if (content) {
            content.style.transform = '';
        }
        this.template.style.backgroundColor = '';

        // Очищаем inline-стили позиционирования ПОСЛЕ анимации
        if (content) {
            const animationDuration = 200;
            setTimeout(() => {
                if (!this.template.classList.contains(this.options.openedClass)) {
                    content.style.position = '';
                    content.style.left = '';
                    content.style.top = '';
                }
            }, animationDuration);
        }
    }

    positionMenu(triggerElement) {
        const content = this.template.querySelector('.card-menu__content');
        if (!content) return;

        // On mobile, don't position - CSS handles bottom sheet
        const isMobile = window.innerWidth <= 768;
        if (isMobile) {
            // Clear any inline styles from previous desktop use
            content.style.position = '';
            content.style.left = '';
            content.style.top = '';
            return;
        }

        if (!triggerElement) return;

        const rect = triggerElement.getBoundingClientRect();

        // Position to the left of the trigger
        const left = rect.left - content.offsetWidth - 10;
        const top = rect.top;

        content.style.position = 'fixed';
        content.style.left = Math.max(10, left) + 'px';
        content.style.top = top + 'px';
    }

    onSiteSearch() {
        if (!this.currentCardElement) return;

        const domainElement = this.currentCardElement.querySelector('.results__card-domain');
        const domain = domainElement?.textContent?.trim();

        if (domain) {
            const searchInput = document.querySelector('[data-searcher-input]');
            const searchButton = document.querySelector('[data-searcher-button]');

            if (searchInput && searchButton) {
                // Add site: operator to search
                const currentQuery = searchInput.value.trim();
                searchInput.value = `site:${domain} ${currentQuery}`.trim();

                // Пересчитать высоту textarea
                if (window.searcherHandle?.resizeAndToggleClear) {
                    window.searcherHandle.resizeAndToggleClear();
                }

                // Trigger search
                searchButton.click();
            }
        }

        this.close();
    }

    onReport() {
        if (!this.currentCardElement) return;

        const titleElement = this.currentCardElement.querySelector('.results__card-title a');
        const url = titleElement?.getAttribute('href');
        const domain = this.currentCardElement.querySelector('.results__card-domain')?.textContent?.trim();

        // Отправляем жалобу на сервер
        this.sendReport({
            uid: this.currentCardId,
            type: 'other',  // По умолчанию "другое", можно добавить выбор типа
            url: url,
            page_url: window.location.href
        }).then(success => {
            if (success) {
                alert(this.options.messages.reportSuccess.replace('{domain}', domain || url));
            } else {
                alert(this.options.messages.reportError);
            }
        });

        this.close();
    }

    /**
     * Отправка жалобы на сервер
     */
    async sendReport(data) {
        try {
            const response = await fetch(this.options.apiEndpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(data)
            });

            const result = await response.json();

            this.log('Жалоба отправлена:', data, result);

            return result.success === true;
        } catch (err) {
            this.log('Ошибка отправки жалобы:', err);
            return false;
        }
    }

    /**
     * Логирование (только если debug: true)
     */
    log(...args) {
        if (this.options.debug) {
            console.log('[CardMenu]', ...args);
        }
    }

    /**
     * Открыть модальное окно продвижения
     */
    async onPromote() {
        const domain = this.currentCardElement?.querySelector('.results__card-domain')?.textContent?.trim();

        if (!domain) {
            this.log('Не найден домен для продвижения');
            return;
        }

        this.close();

        // Показываем модалку сразу с лоадером
        this.showPromoteModal(null, domain);

        // Загружаем данные асинхронно
        let siteInfo = { level: 0, domain: domain };
        try {
            const response = await fetch(`${this.options.apiPromoteInfo}?domain=${encodeURIComponent(domain)}`);
            if (response.ok) {
                siteInfo = await response.json();
            }
        } catch (err) {
            this.log('Ошибка получения информации о сайте:', err);
        }

        // Обновляем модалку данными
        this.updatePromoteModalData(siteInfo, domain);
    }

    /**
     * Показать модальное окно продвижения
     */
    showPromoteModal(siteInfo, domain) {
        // Удалить предыдущее модальное окно если есть
        if (this.promoteModal) {
            this.promoteModal.remove();
        }

        const isLoading = siteInfo === null;

        this.promoteModal = document.createElement('div');
        this.promoteModal.className = 'promote-modal';
        this.promoteModal.innerHTML = `
            <div class="promote-modal__backdrop"></div>
            <div class="promote-modal__content">
                <div class="promote-modal__header">
                    <h2 class="promote-modal__title">Повышение приоритета сайта</h2>
                    <button class="promote-modal__close" type="button" aria-label="Закрыть">&times;</button>
                </div>
                
                <div class="promote-modal__body">
                    <div class="promote-modal__info">
                        <p>Сайты в поиске ранжируются по уровням приоритета. Сайты с более высоким уровнем 
                        всегда отображаются выше сайтов с более низким уровнем среди релевантных результатов.</p>
                        
                        <p>Обычные сайты находятся на базовом уровне (0). Вы можете повысить приоритет 
                        выбранного сайта на +1 к текущему уровню на выбранный период.</p>
                    </div>

                    <div class="promote-modal__form-wrapper" data-promote-form-wrapper>
                        ${isLoading ? this.getPromoteFormSkeleton() : this.getPromoteFormHtml(siteInfo, domain)}
                    </div>
                </div>
            </div>
        `;

        document.body.appendChild(this.promoteModal);

        // Блокируем скролл body
        document.body.style.overflow = 'hidden';
        document.body.style.touchAction = 'none';

        // Обработчики
        const backdrop = this.promoteModal.querySelector('.promote-modal__backdrop');
        const closeBtn = this.promoteModal.querySelector('.promote-modal__close');

        backdrop.addEventListener('click', () => this.closePromoteModal());
        closeBtn.addEventListener('click', () => this.closePromoteModal());

        // Привязываем обработчики формы если не loading
        if (!isLoading) {
            this.attachPromoteFormHandlers();
        }

        // Закрытие по Escape
        this.promoteModalEscHandler = (e) => {
            if (e.key === 'Escape') {
                this.closePromoteModal();
            }
        };
        document.addEventListener('keydown', this.promoteModalEscHandler);

        // Проверка высоты на мобильных
        this.checkPromoteModalHeight();
        this.promoteModalResizeHandler = () => this.checkPromoteModalHeight();
        window.addEventListener('resize', this.promoteModalResizeHandler);

        // Анимация появления
        requestAnimationFrame(() => {
            this.promoteModal.classList.add('_visible');
        });
    }

    /**
     * Получить HTML скелетона формы
     */
    getPromoteFormSkeleton() {
        return `
            <div class="promote-modal__skeleton">
                <div class="promote-modal__skeleton-row"></div>
                <div class="promote-modal__skeleton-row"></div>
                <div class="promote-modal__skeleton-row"></div>
                <div class="promote-modal__skeleton-row promote-modal__skeleton-row_short"></div>
            </div>
        `;
    }

    /**
     * Получить HTML формы с данными
     */
    getPromoteFormHtml(siteInfo, domain) {
        const levelText = siteInfo.level === 0
            ? 'Базовый (0)'
            : `Уровень ${siteInfo.level}`;

        return `
                    <form class="promote-modal__form">
                        <input type="hidden" name="domain" value="${this.escapeHtml(domain)}">
                        
                        <div class="promote-modal__field">
                            <label class="promote-modal__label">Сайт</label>
                            <div class="promote-modal__value">${this.escapeHtml(domain)}</div>
                        </div>

                        <div class="promote-modal__field">
                            <label class="promote-modal__label">Текущий уровень</label>
                            <div class="promote-modal__value promote-modal__level">${levelText}</div>
                        </div>

                        <div class="promote-modal__field">
                            <label class="promote-modal__label">Новый уровень после оплаты</label>
                            <div class="promote-modal__value promote-modal__level promote-modal__level--new">Уровень ${siteInfo.level + 1}</div>
                        </div>

                        <div class="promote-modal__field">
                            <label class="promote-modal__label" for="promote-period">Период действия</label>
                            <select class="promote-modal__select" id="promote-period" name="period" required>
                                <option value="30">30 дней</option>
                                <option value="90">90 дней</option>
                                <option value="365">1 год</option>
                            </select>
                        </div>

                        <div class="promote-modal__field">
                            <label class="promote-modal__label" for="promote-phone">Номер телефона</label>
                            <input class="promote-modal__input" type="tel" id="promote-phone" name="phone" inputmode="tel"
                                   placeholder="+7 (___) ___-__-__" required>
                        </div>

                        <div class="promote-modal__field promote-modal__field--checkbox">
                            <label class="promote-modal__checkbox-label">
                                <input type="checkbox" name="agreement" required>
                                <span>Я ознакомился с <a href="${this.escapeHtml(this.options.promoteAgreementUrl)}" 
                                      target="_blank" rel="noopener">условиями оплаты</a></span>
                            </label>
                        </div>

                        <div class="promote-modal__actions">
                            <button type="button" class="promote-modal__btn promote-modal__btn--cancel">Отмена</button>
                            <button type="submit" class="promote-modal__btn promote-modal__btn--submit">Продолжить</button>
                        </div>
                    </form>
        `;
    }

    /**
     * Обновить данные в модалке после загрузки API
     */
    updatePromoteModalData(siteInfo, domain) {
        if (!this.promoteModal) return;

        const wrapper = this.promoteModal.querySelector('[data-promote-form-wrapper]');
        if (!wrapper) return;

        wrapper.innerHTML = this.getPromoteFormHtml(siteInfo, domain);
        this.attachPromoteFormHandlers();
    }

    /**
     * Привязать обработчики к форме
     */
    attachPromoteFormHandlers() {
        if (!this.promoteModal) return;

        const cancelBtn = this.promoteModal.querySelector('.promote-modal__btn--cancel');
        const form = this.promoteModal.querySelector('.promote-modal__form');

        if (cancelBtn) {
        cancelBtn.addEventListener('click', () => this.closePromoteModal());
        }

        if (form) {
        form.addEventListener('submit', (e) => {
            e.preventDefault();
            this.submitPromote(form);
        });
            }

        // Пересчёт высоты после появления формы
        this.checkPromoteModalHeight();

        // Фокус на первый интерактивный элемент
        const firstInput = this.promoteModal.querySelector('select, input');
        if (firstInput) {
            firstInput.focus();
        }
    }

    /**
     * Проверить высоту модалки и включить fullscreen если нужно
     */
    checkPromoteModalHeight() {
        if (!this.promoteModal) return;

        const content = this.promoteModal.querySelector('.promote-modal__content');
        if (!content) return;

        const viewportHeight = window.innerHeight;
        const contentHeight = content.scrollHeight;
        const threshold = 100; // Запас в пикселях

        // На мобильных (ширина <= 480px) проверяем нужен ли fullscreen
        if (window.innerWidth <= 480) {
            if (contentHeight + threshold > viewportHeight) {
                this.promoteModal.classList.add('_fullscreen');
            } else {
                this.promoteModal.classList.remove('_fullscreen');
            }
        } else {
            this.promoteModal.classList.remove('_fullscreen');
        }
    }

    /**
     * Закрыть модальное окно продвижения
     */
    closePromoteModal() {
        if (!this.promoteModal) return;

        // Добавляем класс для анимации закрытия
        this.promoteModal.classList.add('_closing');
        this.promoteModal.classList.remove('_visible');

        // Ждём окончания анимации перед удалением
        setTimeout(() => {
        if (this.promoteModal) {
            this.promoteModal.remove();
            this.promoteModal = null;
        }
        }, 200);

        if (this.promoteModalEscHandler) {
            document.removeEventListener('keydown', this.promoteModalEscHandler);
            this.promoteModalEscHandler = null;
        }
        if (this.promoteModalResizeHandler) {
            window.removeEventListener('resize', this.promoteModalResizeHandler);
            this.promoteModalResizeHandler = null;
        }

        // Разблокируем скролл body
        document.body.style.overflow = '';
        document.body.style.touchAction = '';
    }

    /**
     * Отправить заявку на продвижение
     */
    async submitPromote(form) {
        const formData = new FormData(form);
        const data = {
            domain: formData.get('domain'),
            period: formData.get('period'),
            phone: formData.get('phone')
        };

        this.log('Заявка на продвижение:', data);

        try {
            const response = await fetch(this.options.apiPromote, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(data)
            });

            const result = await response.json();

            if (result.success && result.paymentUrl) {
                // Перенаправить на страницу оплаты
                window.location.href = result.paymentUrl;
            } else {
                alert(result.error || 'Произошла ошибка. Попробуйте позже.');
            }
        } catch (err) {
            this.log('Ошибка отправки заявки:', err);
            alert('Не удалось отправить заявку. Попробуйте позже.');
        }
    }

    /**
     * Экранирование HTML
     */
    escapeHtml(str) {
        if (!str) return '';
        return str
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#039;');
    }
}

export default CardMenu;